// Global
import React from 'react';
import Head from 'next/head';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { isError } from 'lib/typescript';
import { logError } from 'lib/util/errors';

const optimizedCssFonts = (() => {
  const sourceCss = `
  @font-face {
    font-display: swap;
    font-family: 'DM Sans';
    src: url('/fonts/dmsans-regular-webfont.woff') format('woff'),
    url('/fonts/dmsans-regular-webfont.woff2') format('woff2');
  }
  @font-face {
      font-display: swap;
      font-family: 'DM Sans';
      font-style: italic;
      src: url('/fonts/dmsans-italic-webfont.woff') format('woff'),
      url('/fonts/dmsans-italic-webfont.woff2') format('woff2');

  }
  @font-face {
      font-display: swap;
      font-family: 'DM Sans';
      font-weight: 500;
      src: url('/fonts/dmsans-medium-webfont.woff') format('woff'),
      url('/fonts/dmsans-medium-webfont.woff2') format('woff2');
  }
  @font-face {
      font-display: swap;
      font-family: 'DM Sans';
      font-style: italic;
      font-weight: 500;
      src: url('/fonts/dmsans-mediumitalic-webfont.woff') format('woff'),
      url('/fonts/dmsans-mediumitalic-webfont.woff2') format('woff2');
  }
  @font-face {
      font-display: swap;
      font-family: 'DM Sans';
      font-weight: 700;
      src: url('/fonts/dmsans-bold-webfont.woff') format('woff'),
      url('/fonts/dmsans-bold-webfont.woff2') format('woff2');
  }
  @font-face {
      font-display: swap;
      font-family: 'DM Sans';
      font-style: italic;
      font-weight: 700;
      src: url('/fonts/dmsans-bolditalic-webfont.woff') format('woff'),
      url('/fonts/dmsans-bolditalic-webfont.woff2') format('woff2');
  }
  @font-face {
      font-display: swap;
      font-family: 'Lora';
      src: url('/fonts/lora-regular-webfont.woff') format('woff'),
      url('/fonts/lora-regular-webfont.woff2') format('woff2');
  }`;
  try {
    // Attempt to trim out CSS unnecessary whitespace for optimal render
    return sourceCss.replaceAll(/\s{2,}|(?<=:)\s/g, '');
  } catch (e: unknown) {
    if (isError(e)) {
      // This expression will fail on iPhone 11 running v16.2 of iOS, possibly additional devices.
      // in this case is fine, just return unoptomized css, but there is potential we see additional
      // impact.
      logError('HeadNRCFonts CSS Optimize', e);
    }
    return sourceCss;
  }
})();

const HeadNRCFonts = (): JSX.Element => {
  const isEE = useExperienceEditor();
  const fontFaceStyles = optimizedCssFonts;
  return (
    <Head>
      {/* Preload our two most heavily used webfonts, reduce chance of FOUT */}
      {!isEE && (
        <>
          <link
            key="nmdp-font-dmsans-regular"
            rel="preload"
            href="/fonts/dmsans-regular-webfont.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            key="nmdp-font-lora-regular"
            rel="preload"
            href="/fonts/lora-regular-webfont.woff"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
        </>
      )}
      {/*
        Load all of our @font-face declarations directly so we have absolute paths
        and fonts load everywhere.
      */}
      <style
        key="nmdp-font-inline"
        // Hardcoded html with static text embedded in component code
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: fontFaceStyles }}
      />
    </Head>
  );
};

export default HeadNRCFonts;
